import React, { createContext, useState, useContext, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { app } from "../firebase"; // Assuming this exports your Firebase app instance
import { initializeUserDocument, checkAndAddTutorialSetting } from '../Screens/Helpers/Tutorials';
import { waitForActiveSignup } from '../services/SignupCoordinator';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  // True until onAuthStateChanged fires at least once — consumers can wait on this
  // before making authorization decisions.
  const [authLoading, setAuthLoading] = useState(true);
  const auth = getAuth(app);
  const db = getFirestore(app);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Skip Firestore operations for anonymous users
        if (user.isAnonymous) {
          setCurrentUser(user);
          // Don't update currentUserData for anonymous users
          setAuthLoading(false);
          return;
        }

        const userDocRef = doc(db, "users", user.email);
        let userDocSnap = await getDoc(userDocRef);

        // A sign-up in progress writes the full profile right after creating the auth user; wait for it
        // instead of creating a placeholder profile that would be missing the onboarding state.
        if (!userDocSnap.exists()) {
          await waitForActiveSignup();
          userDocSnap = await getDoc(userDocRef);
        }

        // Check if this is a new user
        if (!userDocSnap.exists()) {
          // Initialize new user document with tutorial enabled
          await initializeUserDocument(user.email);
        } else {
          // Existing user - check and add tutorial setting if missing
          await checkAndAddTutorialSetting(user.email);
        }

        // Continue with existing logic — for new users, re-fetch the doc we just created
        const finalDocSnap = userDocSnap.exists() ? userDocSnap : await getDoc(userDocRef);
        if (finalDocSnap.exists()) {
          const userData = finalDocSnap.data();
          setCurrentUser(user);
          setCurrentUserData(userData);
          localStorage.setItem('currentUser', JSON.stringify(user));
          localStorage.setItem('currentUserData', JSON.stringify(userData));
        } else {
          // Document couldn't be verified, still expose the auth user
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
        setCurrentUserData(null);
        localStorage.removeItem('currentUser');
        localStorage.removeItem('currentUserData');
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [auth, db]);

  // Keep the cache in step with every in-app update, otherwise a page refresh briefly (or, if the
  // fresh fetch fails, permanently) restores stale data such as the previous account type.
  useEffect(() => {
    if (currentUserData) {
      localStorage.setItem('currentUserData', JSON.stringify(currentUserData));
    }
  }, [currentUserData]);

  useEffect(() => {
    // alert("Checking for cached user data...");
    const cachedUser = localStorage.getItem('currentUser');
    const cachedUserData = localStorage.getItem('currentUserData');
    if (cachedUser && cachedUserData) {
      const userData = JSON.parse(cachedUserData);
      setCurrentUser(JSON.parse(cachedUser));
      setCurrentUserData(userData);
      // alert(`Welcome ${userData.firstName}`);
      // alert("Cached user data found and set.");
    } else {
      // alert("No cached user data found.");
    }
  }, []);

  return (
    <UserContext.Provider value={{ currentUser, currentUserData, authLoading, setCurrentUser, setCurrentUserData }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);